import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

import {
  locationSalesGetTaxCollected,
  type TaxCollectedEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

export const Route = createFileRoute(
  "/_authenticated/reports/tax-collected-report"
)({
  component: TaxCollectedReportPage,
});

type RowData = TaxCollectedEntry;

function TaxCollectedReportPage() {
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Tax
            Collected Report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">Tax Collected Report</h2>
        <p className="body text-muted-foreground">
          View tax collection data by tax category
        </p>
      </div>

      <TaxCollectedReportContent locationId={locationId} />
    </div>
  );
}

const columnHelper = createColumnHelper<RowData>();

const columns = [
  columnHelper.accessor("location", {
    header: "Location Name",
    cell: ({ getValue }) => getValue().name,
    filterFn: ({ original }, _, filterValue: string[]) =>
      filterValue.includes(original.location.id),
    meta: {
      faceting: {
        title: "Location",
        getOption: (r: RowData["location"]) => ({
          value: r.id,
          label: r.name,
        }),
      },
      getCellForExport: (r: RowData["location"]) => r.name,
    },
  }),
  columnHelper.accessor("taxCategory", {
    header: "Tax Category",
    cell: ({ getValue }) => getValue().name,
    filterFn: ({ original }, _, filterValue: string[]) =>
      filterValue.includes(original.taxCategory.id),
    meta: {
      faceting: {
        title: "Tax Category",
        getOption: (r: RowData["taxCategory"]) => ({
          value: r.id,
          label: r.name,
        }),
      },
      getCellForExport: (r: RowData["taxCategory"]) => r.name,
    },
  }),
  columnHelper.accessor("taxCategory.taxCode", {
    header: "Tax Code",
  }),
  columnHelper.accessor("taxCategory.value", {
    header: "Tax %",
    cell: ({ getValue }) => `${getValue()}%`,
  }),
  columnHelper.accessor("amountSold", {
    header: "Taxable Goods Sales",
    cell: ({ getValue }) =>
      getValue()?.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
    meta: {
      getCellForExport: (d: RowData["amountSold"]) => d,
    },
  }),
  columnHelper.accessor("itemsSold", {
    header: "Total Items Ordered",
    cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
    meta: {
      getCellForExport: (d: RowData["itemsSold"]) => d,
    },
  }),
  columnHelper.accessor("taxCollected", {
    header: "Tax Collected",
    cell: ({ getValue }) =>
      getValue()?.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
    meta: {
      getCellForExport: (d: RowData["taxCollected"]) => d,
    },
  }),
] as ColumnDef<RowData>[];

const TaxCollectedReportContent = ({ locationId }: { locationId: string }) => {
  const [dateRange] = useSearchParamsDateRange();

  const { data, isPending: isLoading } = useQuery(
    convexQuery(locationSalesGetTaxCollected, {
      dateRange: {
        from: dateRange.from?.getTime() ?? 0,
        to: dateRange.to?.getTime() ?? 0,
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  if (isLoading) {
    return <LoadingPanel message="Loading tax collected data..." />;
  }

  return (
    <SalesDataTable
      columns={columns}
      data={data ?? []}
      id="tax-collected-report-table"
    />
  );
};
